import { useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Schema.org JSON-LD for Privacy Policy page
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Política de Privacidad - Zhongming Import SL",
      "description": "Política de privacidad y protección de datos de Zhongming Import SL. Información sobre el tratamiento de datos personales según RGPD.",
      "url": `${import.meta.env.VITE_SITE_URL || "https://zhongmingimport.com"}/privacy-policy`,
      "inLanguage": "es",
      "isPartOf": {
        "@type": "WebSite",
        "name": "Zhongming Import SL",
        "url": import.meta.env.VITE_SITE_URL || "https://zhongmingimport.com"
      },
      "about": {
        "@type": "Organization",
        "name": "Zhongming Import SL",
        "legalName": "Zhongming Import SL",
        "taxID": "B75817635"
      },
      "dateModified": "2024-01-15",
      "datePublished": "2024-01-15"
    };

    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);

    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <header>
            <h1 className="text-4xl md:text-5xl font-bold text-blue-900 mb-6">
              Política de Privacidad
            </h1>
          </header>
          
          <div className="text-gray-600 mb-8">
            <p className="mb-2"><strong>Última actualización:</strong> 15 de enero de 2024</p>
            <p><strong>Empresa:</strong> Zhongming Import SL</p>
            <p><strong>CIF:</strong> B75817635</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">1. Responsable del Tratamiento</h2>
              <div className="bg-blue-50 p-6 rounded-lg mb-4">
                <p className="mb-2"><strong>Identidad:</strong> Zhongming Import SL</p>
                <p className="mb-2"><strong>CIF:</strong> B75817635</p>
                <p className="mb-2"><strong>Dirección:</strong> C/ PICO DE LA CIERVA 1, 28031 - MADRID</p>
                <p className="mb-2"><strong>Teléfono:</strong> +34 688 06 61 41</p>
                <p><strong>Email:</strong> info@zhongmingimport.com</p>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Zhongming Import SL es el responsable del tratamiento de los datos personales que nos proporcione a través de este sitio web y se compromete a cumplir con el Reglamento General de Protección de Datos (RGPD) y la Ley Orgánica de Protección de Datos y Garantía de los Derechos Digitales (LOPDGDD).
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">2. Datos que Recopilamos</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Recopilamos y tratamos los siguientes tipos de datos personales:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span><strong>Datos de identificación:</strong> Nombre completo, empresa, CIF/NIF</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span><strong>Datos de contacto:</strong> Email profesional, teléfono, dirección postal</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span><strong>Datos comerciales:</strong> Tipo de proyecto, necesidades específicas, presupuesto estimado</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span><strong>Datos de navegación:</strong> Dirección IP, cookies, datos de uso del sitio web</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">3. Finalidad del Tratamiento</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Sus datos personales serán tratados para las siguientes finalidades:
              </p>
              <div className="space-y-4">
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2">Gestión de Consultas y Solicitudes</h3>
                  <p className="text-gray-700">Responder a sus consultas, solicitudes de información y presupuestos relacionados con nuestros servicios de importación de materiales de construcción.</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2">Relación Comercial B2B</h3>
                  <p className="text-gray-700">Gestionar la relación comercial, envío de ofertas, catálogos, información técnica y seguimiento de proyectos profesionales.</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2">Comunicaciones Comerciales</h3>
                  <p className="text-gray-700">Envío de newsletters, novedades de productos, promociones especiales y contenido relevante para profesionales del sector (solo con su consentimiento previo).</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2">Mejora de Servicios</h3>
                  <p className="text-gray-700">Análisis de uso del sitio web para mejorar nuestros servicios y experiencia de usuario.</p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">4. Base Legal del Tratamiento</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                El tratamiento de sus datos se basa en:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span><strong>Consentimiento del interesado:</strong> Al enviar el formulario de contacto o suscribirse a nuestras comunicaciones</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span><strong>Ejecución de un contrato:</strong> Para la gestión de pedidos y relación comercial B2B</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span><strong>Interés legítimo:</strong> Para el análisis y mejora de nuestros servicios profesionales</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span><strong>Obligación legal:</strong> Cumplimiento de obligaciones fiscales y contables</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">5. Conservación de Datos</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Los datos personales se conservarán durante los siguientes plazos:
              </p>
              <div className="bg-blue-50 p-6 rounded-lg space-y-3">
                <p className="text-gray-700"><strong>Consultas y presupuestos:</strong> 2 años desde la última comunicación</p>
                <p className="text-gray-700"><strong>Relación comercial activa:</strong> Durante la vigencia de la relación comercial</p>
                <p className="text-gray-700"><strong>Obligaciones legales:</strong> 6 años según normativa fiscal y mercantil</p>
                <p className="text-gray-700"><strong>Comunicaciones comerciales:</strong> Hasta que revoque su consentimiento</p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">6. Destinatarios de los Datos</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Sus datos podrán ser comunicados a:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-building-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span><strong>Proveedores de servicios:</strong> Empresas de hosting, servicios de email marketing, plataformas de gestión (bajo acuerdos de confidencialidad)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-government-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span><strong>Administraciones públicas:</strong> Cuando exista obligación legal (Agencia Tributaria, Seguridad Social, etc.)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-global-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span><strong>Oficina en China:</strong> Para gestión de pedidos y control de calidad (con garantías adecuadas de protección)</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                No cedemos ni vendemos sus datos personales a terceros con fines comerciales.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">7. Sus Derechos</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Puede ejercer los siguientes derechos en cualquier momento:
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                    <i className="ri-eye-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Acceso
                  </h3>
                  <p className="text-gray-700 text-sm">Conocer qué datos personales tenemos sobre usted</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                    <i className="ri-edit-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Rectificación
                  </h3>
                  <p className="text-gray-700 text-sm">Corregir datos inexactos o incompletos</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                    <i className="ri-delete-bin-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Supresión
                  </h3>
                  <p className="text-gray-700 text-sm">Solicitar la eliminación de sus datos</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                    <i className="ri-forbid-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Oposición
                  </h3>
                  <p className="text-gray-700 text-sm">Oponerse al tratamiento de sus datos</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                    <i className="ri-pause-circle-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Limitación
                  </h3>
                  <p className="text-gray-700 text-sm">Limitar el tratamiento de sus datos</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                    <i className="ri-download-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Portabilidad
                  </h3>
                  <p className="text-gray-700 text-sm">Recibir sus datos en formato estructurado</p>
                </div>
              </div>
              <div className="bg-blue-900 text-white p-6 rounded-lg">
                <h3 className="font-bold mb-3">¿Cómo ejercer sus derechos?</h3>
                <p className="mb-3">Puede ejercer sus derechos enviando un email a:</p>
                <p className="text-lg font-semibold mb-3">info@zhongmingimport.com</p>
                <p className="text-sm text-blue-100">Incluya copia de su DNI/NIE o documento identificativo y especifique el derecho que desea ejercer. Responderemos en un plazo máximo de 1 mes.</p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">8. Seguridad de los Datos</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Zhongming Import SL ha implementado medidas técnicas y organizativas apropiadas para garantizar la seguridad de sus datos personales:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>Cifrado SSL/TLS en todas las comunicaciones</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>Acceso restringido a datos personales solo a personal autorizado</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>Copias de seguridad periódicas y sistemas de recuperación</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>Auditorías de seguridad regulares</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">9. Cookies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Este sitio web utiliza cookies técnicas necesarias para su funcionamiento. Para más información sobre el uso de cookies, consulte nuestra Política de Cookies.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">10. Reclamaciones</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Si considera que el tratamiento de sus datos personales no se ajusta a la normativa, puede presentar una reclamación ante la Agencia Española de Protección de Datos (AEPD):
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>Agencia Española de Protección de Datos</strong></p>
                <p className="text-gray-700 mb-2">C/ Jorge Juan, 6</p>
                <p className="text-gray-700 mb-2">28001 - Madrid</p>
                <p className="text-gray-700 mb-2">Web: www.aepd.es</p>
                <p className="text-gray-700">Tel: 901 100 099 / 912 663 517</p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">11. Actualizaciones</h2>
              <p className="text-gray-700 leading-relaxed">
                Zhongming Import SL se reserva el derecho de modificar esta Política de Privacidad para adaptarla a cambios legislativos o jurisprudenciales. Le recomendamos revisar periódicamente esta página. La fecha de la última actualización se indica al inicio del documento.
              </p>
            </section>

            <div className="bg-blue-50 border-l-4 border-blue-900 p-6 rounded-lg">
              <p className="text-gray-700 leading-relaxed">
                <i className="ri-information-line text-blue-900 mr-2"></i>
                <strong>¿Tiene dudas sobre nuestra Política de Privacidad?</strong><br />
                Contacte con nosotros en <a href="mailto:info@zhongmingimport.com" className="text-blue-900 font-semibold hover:underline">info@zhongmingimport.com</a> o llámenos al <strong>+34 688 06 61 41</strong>
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
